// DS: MO10.4
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Url } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiData, MessageIconClickBiEvent } from '@wix/da-bi/pkg/events';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton/BrandedButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton/IconButton';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import DescTiptapViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/DeviationDescription';
import TextRenderer from '../../TextRenderer';
import CollapsibleBlock from '../../CollapsibleBlock/CollapsibleBlock';
import DeviationDumbView from '../../DeviationViews/Dumbo';
import DeviationLink from '../../Duperbrowse/DeviationLink/DeviationLink';
import { getIsCurrentUserAuthorOf } from '../../redux/deviations/selectors';
import { formatDollars } from '../../utils/formatting';
import { normalizeOriginalsCompletedOrders } from '../../utils/originalsCompletedOrders';
import RequestOriginalButton from '../../originals/RequestOriginalButton';
import CompletedOrdersThumbs from './_partials/CompletedOrdersThumbs';

import s from './OriginalsExpandedShopCard.scss';

interface Props {
  deviation: PapiDeviation;
}

export const OriginalsExpandedShopCard: React.FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { width: screenWidth } = useContext(ScreenSizeContext);
  const thumbWidth = isMobile ? screenWidth : 355;
  const thumbHeight = isMobile ? 208 : 215;

  const isOwner = useSelector(state => getIsCurrentUserAuthorOf(state, deviation));

  const originals = deviation.originals;
  if (!originals) {
    return null;
  }

  const { basePrice, showInProgressCount, inProgressOrdersCount = 0, description } = originals;
  const {
    deviations: completedOrdersDeviations,
    total: completedOrdersCount,
    hasMore: hasMoreCompletedOrders,
  } = normalizeOriginalsCompletedOrders(originals);

  return (
    <div className={s['root']}>
      <div className={s['thumbs']}>
        <DeviationLink deviation={deviation} key={deviation.deviationId}>
          <DeviationDumbView
            className={s['cover']}
            deviation={deviation}
            width={thumbWidth}
            height={thumbHeight}
          />
        </DeviationLink>
        {!isMobile && (
          <CompletedOrdersThumbs
            parentDeviation={deviation}
            deviations={completedOrdersDeviations}
            hasMoreDeviations={hasMoreCompletedOrders}
          />
        )}
      </div>

      <div className={s['details']}>
        <div className={s['main-details']}>
          <DeviationLink deviation={deviation} className={s['title']}>
            {deviation.title}
          </DeviationLink>

          <div className={s['price-and-total']}>
            <div className={s['price']}>
              {t('shop.card.originals.basePrice')} {formatDollars(basePrice)}
            </div>

            {completedOrdersCount > 0 && (
              <div className={s['total-orders']}>
                {t('shop.thumb.originals.totalOrders', {
                  count: completedOrdersCount,
                })}
              </div>
            )}

            {showInProgressCount && inProgressOrdersCount > 0 && (
              <div className={s['in-progress-orders']}>
                {t('shop.thumb.originals.inProgressOrders', {
                  count: inProgressOrdersCount,
                })}
              </div>
            )}
          </div>
        </div>

        <div className={s['actions']}>
          {!isOwner && (
            <IconButton
              icon={MailIcon}
              href={Url.userMessageLink(deviation.author?.username ?? '')}
              size="large"
              aria-label={t('shop.card.originals.requestButton')}
              variant="originals-ring"
              biData={BiData<MessageIconClickBiEvent>({
                evid: 470,
                recipientid: deviation.author?.useridUuid ?? '',
                sectionname: 'originals',
              })}
            />
          )}
          {!isOwner && (
            <RequestOriginalButton deviation={deviation} size="large" action="redirect" />
          )}
          {isOwner && (
            <BrandedButton
              className={s['branded-button']}
              variant="originals-outline"
              size="large"
              href={Url.studio({
                section: 'orders-originals',
                deviationId: deviation.deviationId,
              })}
            >
              {t('originals.actions.manageOrders')}
            </BrandedButton>
          )}
          {isOwner && (
            <BrandedButton
              className={s['branded-button']}
              variant="originals"
              size="large"
              href={Url.submissionLinkWithId(deviation.deviationId)}
            >
              {t('common.edit')}
            </BrandedButton>
          )}
        </div>

        <div className={s['description']}>
          <CollapsibleBlock maxHeight={90} buttonTextCollapsed={t('shop.card.originals.readMore')}>
            <TextRenderer
              className={s['description-text']}
              textContent={description}
              tipTapViewer={DescTiptapViewer}
            />
          </CollapsibleBlock>
        </div>

        {isMobile && (
          <CompletedOrdersThumbs
            parentDeviation={deviation}
            deviations={completedOrdersDeviations}
          />
        )}
      </div>
    </div>
  );
};
OriginalsExpandedShopCard.displayName = 'OriginalsExpandedShopCard';

export default OriginalsExpandedShopCard;
